// Global Instructions Rule Applied!
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import { UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import type { DealRecord } from '../../../state_manager/data_models/deal_record.ts'
import { CURRENCY_USD, DEFAULT_PIPELINE_STAGE_ID } from '../../../state_manager/globals.ts'
import { UNKNOWN_CONTACT, UNKNOWN_USER } from '../../../state_manager/constants/strings.ts'
import { companyTeamEntryToMember } from '../../../core/components/CompanyTeamTable/utils/utils.ts'

export type SelectedContactsShape = {
  introduced_by: any[]
  sourced_by: any[]
  deal_team_lead: any[]
}

const DEFAULT_SELECTED_CONTACTS: SelectedContactsShape = {
  introduced_by: [],
  sourced_by: [],
  deal_team_lead: []
}

/**
 * Get initial form data for new deal
 * @param defaults - caller-supplied defaults (e.g. deal type resolved from lookups) merged over the base values
 */
export function getInitialFormDataForNew(defaults: Record<string, any> = {}): Record<string, any> {
  return {
    [DealFields.deals__name]: '',
    [DealFields.deals__account]: '',
    [DealFields.prog__is_new_account]: false,
    [DealFields.accounts__industry]: '',
    [DealFields.accounts__website]: '',
    [DealFields.accounts__business_city_code]: '',
    [DealFields.accounts__business_country_code]: '',
    [DealFields.accounts__business_stateprovince]: '',
    [DealFields.accounts__description]: '',
    [DealFields.rounds__is_funding_round]: true,
    [DealFields.deals__currency]: CURRENCY_USD,
    [DealFields.deals__allocation_available]: '',
    [DealFields.rounds__raise_amount]: '',
    [DealFields.rounds__pre_money_valuation]: '',
    [DealFields.rounds__post_money_valuation]: '',
    [DealFields.rounds__name]: '',
    [DealFields.rounds__funding_stage]: '',
    [DealFields.pipeline_deals__stage]: DEFAULT_PIPELINE_STAGE_ID,
    ...defaults
  }
}

/**
 * Merge deal into initial form data; strip nested collections for form state and
 * return them separately.
 *
 * Accepts a seed in EITHER shape:
 *
 *   * the drawer's own state names — `commitments`, `selectedContacts`,
 *     `companyTeamMembers` — as the quick-add overlay has always passed them; or
 *   * the CANONICAL RECORD shape the rest of the system speaks:
 *     `prog__deal_commitments`, `prog__company_team`, and the three
 *     contact-role fields. That is what `process_new_deal` consumes, what this
 *     drawer already EMITS on submit, and therefore what an agent proposal's
 *     payload holds — so the review form can seed straight from it without an
 *     adapter of its own (owner 2026-09-02).
 *
 * Drawer-state keys win where a seed somehow carries both, because that is the
 * shape a caller passes deliberately.
 */
export function mergeDealIntoInitialFormData(
  deal: any,
  defaults: Record<string, any> = {}
): {
  formData: Record<string, any>
  commitments: any[]
  selectedContacts: SelectedContactsShape
  companyTeamMembers: any[]
} {
  const initialData = getInitialFormDataForNew(defaults)
  if (!deal || Object.keys(deal).length === 0) {
    return {
      formData: initialData,
      commitments: [],
      selectedContacts: DEFAULT_SELECTED_CONTACTS,
      companyTeamMembers: []
    }
  }
  const {
    // Canonical record keys are pulled out here so they never reach formData —
    // they are collections, and the form's field state holds scalars.
    [DealFields.prog__deal_commitments]: recordCommitments,
    [DealFields.prog__company_team]: recordCompanyTeam,
    [DealFields.contact_accounts__introduced_by]: recordIntroducedBy,
    [DealFields.contact_accounts__sourced_by]: recordSourcedBy,
    [DealFields.contact_deals__lead]: recordDealTeamLead,
    commitments = recordCommitments ?? [],
    selectedContacts = null,
    companyTeamMembers = recordCompanyTeam ?? [],
    ...dealDataWithoutNested
  } = deal

  // Only build role buckets from the record fields when the caller did not
  // hand us drawer-shaped ones.
  const resolvedSelectedContacts =
    selectedContacts ??
    (recordIntroducedBy || recordSourcedBy || recordDealTeamLead
      ? {
          introduced_by: recordIntroducedBy ?? [],
          sourced_by: recordSourcedBy ?? [],
          deal_team_lead: recordDealTeamLead ?? []
        }
      : DEFAULT_SELECTED_CONTACTS)
  return {
    formData: { ...initialData, ...dealDataWithoutNested },
    commitments: Array.isArray(commitments) ? commitments : [],
    selectedContacts:
      resolvedSelectedContacts && typeof resolvedSelectedContacts === 'object'
        ? {
            introduced_by: resolvedSelectedContacts.introduced_by ?? [],
            sourced_by: resolvedSelectedContacts.sourced_by ?? [],
            deal_team_lead: resolvedSelectedContacts.deal_team_lead ?? []
          }
        : DEFAULT_SELECTED_CONTACTS,
    // Record entries become table rows here; the table cannot render the
    // record shape (see companyTeamEntryToMember).
    companyTeamMembers: Array.isArray(companyTeamMembers) ? companyTeamMembers.map(companyTeamEntryToMember) : []
  }
}

/**
 * Default selected contacts; optionally fill sourced_by and/or deal_team_lead from currentUser
 */
export function getDefaultSelectedContacts(
  currentUser: any | null,
  options?: { sourcedBy?: boolean; dealTeamLead?: boolean }
): SelectedContactsShape {
  const sourcedBy = options?.sourcedBy !== false
  const dealTeamLead = options?.dealTeamLead !== false
  const result: SelectedContactsShape = { ...DEFAULT_SELECTED_CONTACTS }
  if (currentUser) {
    const contact = transformSystemUserToContact(currentUser)
    if (sourcedBy) result.sourced_by = [contact]
    if (dealTeamLead) result.deal_team_lead = [contact]
  }
  return result
}

/**
 * Transform UserEntry to form contact format (optionally include raw userEntry as data)
 */
export function transformUserEntryToContact(userEntry: any, includeData = false): any {
  const displayName =
    userEntry[UserEntryFields.contacts__preferred_name] || userEntry[UserEntryFields.contacts__first_name] || ''
  const fullName = userEntry[UserEntryFields.contacts__last_name]
    ? `${displayName} ${userEntry[UserEntryFields.contacts__last_name]}`.trim()
    : displayName

  const result = {
    label: fullName || userEntry[UserEntryFields.contacts__email] || UNKNOWN_CONTACT,
    value: userEntry[UserEntryFields.contacts__id] || '',
    email: userEntry[UserEntryFields.contacts__email] || '',
    first_name: userEntry[UserEntryFields.contacts__first_name] || '',
    last_name: userEntry[UserEntryFields.contacts__last_name] || '',
    preferred_name: userEntry[UserEntryFields.contacts__preferred_name] || '',
    full_name: userEntry[UserEntryFields.prog__display_name] || fullName
  }
  if (includeData) {
    return { ...result, data: userEntry }
  }
  return result
}

/**
 * Transform system user to form contact format
 */
export function transformSystemUserToContact(systemUser: any): any {
  return {
    label:
      systemUser[UserEntryFields.prog__display_name] || systemUser[UserEntryFields.contacts__email] || UNKNOWN_USER,
    value: systemUser[UserEntryFields.contacts__id] || '',
    email: systemUser[UserEntryFields.contacts__email] || '',
    full_name: systemUser[UserEntryFields.prog__display_name] || '',
    last_name: systemUser[UserEntryFields.contacts__last_name] || ''
  }
}
